import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, Zap, Infinity as InfinityIcon } from 'lucide-react';
import { API, useAuth, checkLastCreditNudge } from '@/App';

const GREEN = '#40916C';

export default function UsageMeter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/payments/usage`, { withCredentials: true })
      .then(({ data }) => { if (alive) setUsage(data); })
      .catch(() => {});
    checkLastCreditNudge();  // one-time nudge if already at the last credit
    return () => { alive = false; };
  }, [user?.plan]);

  if (!usage) return null;

  // Pro / admin — subtle unlimited badge
  if (usage.unlimited) {
    return (
      <div className="max-w-[1320px] mx-auto px-6 pt-6" data-testid="usage-meter">
        <div className="flex items-center gap-2.5 rounded-xl px-4 py-2.5 w-fit"
          style={{ background: 'rgba(64,145,108,0.09)', border: '1px solid rgba(64,145,108,0.2)' }}
          data-testid="usage-meter-pro">
          <Crown size={15} style={{ color: GREEN }} />
          <span className="font-body" style={{ fontSize: 13, fontWeight: 600, color: '#2D6A4F' }}>Pro</span>
          <span className="font-body flex items-center gap-1" style={{ fontSize: 12.5, color: 'var(--text-medium)' }}>
            · <InfinityIcon size={13} /> Unlimited analyses
          </span>
        </div>
      </div>
    );
  }

  const { used, limit } = usage;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const remaining = Math.max(0, limit - used);
  const atLimit = remaining === 0;
  const nearLimit = remaining <= 1;
  const barColor = atLimit ? '#DC2626' : nearLimit ? '#D97706' : GREEN;

  return (
    <div className="max-w-[1320px] mx-auto px-6 pt-6" data-testid="usage-meter">
      <div className="rounded-xl bg-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        style={{ border: `1px solid ${atLimit ? 'rgba(220,38,38,0.3)' : 'var(--border-color)'}` }}
        data-testid="usage-meter-free">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(64,145,108,0.1)' }}>
            <Zap size={17} style={{ color: barColor }} />
          </div>
          <div>
            <div className="font-body" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-dark)' }}>
              Free plan
            </div>
            <div className="font-body" style={{ fontSize: 12, color: 'var(--text-medium)' }} data-testid="usage-count">
              {used} of {limit} analyses used
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="w-full rounded-full h-2.5 overflow-hidden" style={{ background: '#EDEBE6' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, background: barColor }} data-testid="usage-bar" />
          </div>
          <div className="font-body mt-1.5" style={{ fontSize: 11.5, color: atLimit ? '#DC2626' : 'var(--text-light)' }}>
            {atLimit
              ? "You've hit your Free plan limit — upgrade to keep analyzing."
              : `${remaining} ${remaining === 1 ? 'analysis' : 'analyses'} left on the Free plan.`}
          </div>
        </div>

        <button
          onClick={() => navigate('/pricing')}
          className="shrink-0 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-body btn-press transition-all"
          style={{ background: GREEN, color: 'white', fontSize: 13, fontWeight: 600, border: 'none' }}
          data-testid="usage-upgrade-button"
        >
          <Crown size={14} /> Upgrade to Pro
        </button>
      </div>
    </div>
  );
}
